import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";

interface Bid {
  _id: Id<"bids">;
  title: string;
  status: "Draft" | "Submitted" | "Approved" | "Rejected" | "In Review";
  value: string;
  description?: string;
  client: {
    company: string;
    contactName: string;
    email: string;
    phone: string;
    title?: string;
    address?: string;
  };
  lastModified: string;
  createdAt: number;
  updatedAt: number;
  orgId: Id<"organizations">;
}

interface EditBidSheetProps {
  isOpen: boolean;
  onClose: () => void;
  bid: Bid;
}

export function EditBidSheet({ isOpen, onClose, bid }: EditBidSheetProps) {
  const [selectedSource, setSelectedSource] = useState<"lead" | "client">("lead");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");

  // Fetch leads and clients from the businesses table
  const leads = useQuery(api.businesses.getLeads) || [];
  const clients = useQuery(api.businesses.getClients) || [];

  const [formData, setFormData] = useState({
    title: "",
    status: "",
    description: "",
    clientCompany: "",
  });

  // Initialize form data when bid prop changes
  useEffect(() => {
    if (bid) {
      setFormData({
        title: bid.title || "",
        status: bid.status || "Draft",
        description: bid.description || "",
        clientCompany: bid.client.company || "",
      });
    }
  }, [bid]);

  // Update form when a company is selected
  useEffect(() => {
    if (selectedSource === "lead" && selectedCompanyId) {
      const lead = leads.find((lead: any) => lead._id === selectedCompanyId);
      if (lead) {
        setFormData((prev) => ({
          ...prev,
          clientCompany: lead.name,
        }));
      }
    } else if (selectedSource === "client" && selectedCompanyId) {
      const client = clients.find((client: any) => client._id === selectedCompanyId);
      if (client) {
        setFormData((prev) => ({
          ...prev,
          clientCompany: client.name,
        }));
      }
    }
  }, [selectedSource, selectedCompanyId, leads, clients]);

  const { toast } = useToast();
  const updateBid = useMutation(api.bids.update);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      await updateBid({
        id: bid._id,
        title: formData.title,
        status: formData.status as "Draft" | "Submitted" | "Approved" | "Rejected" | "In Review",
        description: formData.description || undefined,
        businessId: selectedCompanyId ? (selectedCompanyId as Id<"businesses">) : undefined,
        client: {
          company: formData.clientCompany,
          contactName: "",
          email: "",
          phone: "",
          title: undefined,
          address: undefined,
        },
      });

      toast({
        title: "Bid updated",
        description: "The bid has been successfully updated.",
        variant: "default",
      });
      onClose();
    } catch (error) {
      console.error("Error updating bid:", error);
      toast({
        title: "Failed to update bid",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose} modal={true}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Bid</SheetTitle>
          <SheetDescription>Update the details for this bid.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="title">Bid Title</Label>
              <Input
                id="title"
                placeholder="Enter bid title"
                value={formData.title}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                onValueChange={(value) => handleSelectChange("status", value)}
                value={formData.status}
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Submitted">Submitted</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                  <SelectItem value="In Review">In Review</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter bid description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
              />
            </div>

            <div className="space-y-6 border-t border-gray-200 py-4">
              <h3 className="text-lg font-medium">Client Information</h3>

              <div className="space-y-2">
                <Label>Company Source</Label>
                <Select
                  value={selectedSource}
                  onValueChange={(value: "lead" | "client") => {
                    setSelectedSource(value);
                    setSelectedCompanyId("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead">Select from Leads</SelectItem>
                    <SelectItem value="client">Select from Clients</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectedSource === "lead" && (
                <div className="space-y-2">
                  <Label htmlFor="lead-select">Select Lead</Label>
                  <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a lead" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Leads</SelectLabel>
                        {leads.length === 0 ? (
                          <SelectItem value="no-leads" disabled>
                            No leads available
                          </SelectItem>
                        ) : (
                          leads.map((lead: any) => (
                            <SelectItem key={lead._id} value={lead._id}>
                              {lead.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedSource === "client" && (
                <div className="space-y-2">
                  <Label htmlFor="client-select">Select Client</Label>
                  <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Clients</SelectLabel>
                        {clients.length === 0 ? (
                          <SelectItem value="no-clients" disabled>
                            No clients available
                          </SelectItem>
                        ) : (
                          clients.map((client: any) => (
                            <SelectItem key={client._id} value={client._id}>
                              {client.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="clientCompany">Company Name</Label>
                <Input
                  id="clientCompany"
                  placeholder="Client company name"
                  value={formData.clientCompany}
                  onChange={handleInputChange}
                  disabled={true}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>Save Changes</Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
